import React, { useEffect, useState } from 'react';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';

const API_BASE = '';

interface AuthStatus {
  authenticated: boolean;
  provider: 'entra' | 'local' | 'none';
  entraConfigured: boolean;
}

const loginStyles = `
@keyframes blobFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%       { transform: translate(-20px, 20px) scale(1.05); }
}
@keyframes cardIn {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

export function LoginPage() {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/api/auth/status`, { credentials: 'include' })
      .then((r) => r.json())
      .then((r) => {
        const data = r.data as AuthStatus;
        setStatus(data);
        if (data.authenticated) {
          window.location.href = '/';
        }
      })
      .catch(() => setStatus({ authenticated: false, provider: 'none', entraConfigured: false }))
      .finally(() => setLoading(false));
  }, []);

  async function handleLocalLogin(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setLoginError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login/local`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setLoginError(
          body?.error?.message ??
            (res.status === 429
              ? 'Demasiados intentos. Espera unos minutos.'
              : 'Credenciales inválidas'),
        );
        return;
      }
      const token = body?.data?.token as string | undefined;
      if (token) {
        localStorage.setItem('vianet-token', token);
      }
      window.location.href = '/';
    } catch {
      setLoginError('Error de red. Reintenta.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div style={containerStyle}>
        <style>{loginStyles}</style>
        <div style={blob1Style} />
        <div style={blob2Style} />
        <div style={cardStyle}>
          <LoadingSpinner size="sm" muted />
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <style>{loginStyles}</style>
      <div style={blob1Style} />
      <div style={blob2Style} />

      <div style={cardStyle}>
        <img
          src="/img/Logo-VIAnet-Color.svg"
          alt="V·IAnet Governance"
          style={{ height: 57, width: 'auto', marginBottom: 36 }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />

        {status?.entraConfigured && (
          <a
            href={`${API_BASE}/api/auth/login`}
            style={msButtonStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 21 21"
              style={{ marginRight: 10, flexShrink: 0 }}
            >
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Iniciar sesion con Microsoft
          </a>
        )}

        {!status?.entraConfigured && (
          <form onSubmit={handleLocalLogin} style={{ width: '100%' }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              autoComplete="username"
              required
              style={{
                width: '100%',
                padding: '12px 14px',
                marginBottom: 10,
                border: '1px solid #D1D5DB',
                borderRadius: 10,
                fontSize: 14,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              autoComplete="current-password"
              required
              style={{
                width: '100%',
                padding: '12px 14px',
                marginBottom: 14,
                border: '1px solid #D1D5DB',
                borderRadius: 10,
                fontSize: 14,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
            {loginError && (
              <div
                style={{
                  padding: '10px 14px',
                  background: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  borderRadius: 8,
                  fontSize: 12,
                  color: '#991B1B',
                  marginBottom: 12,
                  width: '100%',
                  textAlign: 'center',
                  boxSizing: 'border-box',
                }}
              >
                {loginError}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '13px 0',
                width: '100%',
                backgroundColor: submitting ? '#64748B' : '#1F3864',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => {
                if (!submitting) (e.currentTarget as HTMLElement).style.background = '#2F5496';
              }}
              onMouseLeave={(e) => {
                if (!submitting) (e.currentTarget as HTMLElement).style.background = '#1F3864';
              }}
            >
              {submitting ? 'Entrando…' : 'Iniciar sesión'}
            </button>
          </form>
        )}
      </div>

      <div
        style={{
          marginTop: 32,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', letterSpacing: 0.5 }}>
          Powered by
        </span>
        <img
          src="/img/Logo-horizontal-Blanco.svg"
          alt="VALOR·IA"
          style={{ height: 30, width: 'auto', opacity: 0.55 }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  background: '#06111C',
  position: 'relative',
  overflow: 'hidden',
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
};

const blob1Style: React.CSSProperties = {
  position: 'absolute',
  top: '-15%',
  right: '-10%',
  width: 500,
  height: 500,
  borderRadius: '50%',
  background: 'rgba(20,184,166,0.35)',
  filter: 'blur(60px)',
  animation: 'blobFloat 8s ease-in-out infinite',
  pointerEvents: 'none',
};

const blob2Style: React.CSSProperties = {
  position: 'absolute',
  bottom: '-20%',
  left: '-12%',
  width: 520,
  height: 520,
  borderRadius: '50%',
  background: 'rgba(13,148,136,0.35)',
  filter: 'blur(60px)',
  animation: 'blobFloat 11s ease-in-out infinite reverse',
  pointerEvents: 'none',
};

const cardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '44px 40px',
  background: '#ffffff',
  borderRadius: 20,
  boxShadow: '0 24px 64px rgba(0,0,0,0.30), 0 8px 24px rgba(0,0,0,0.12)',
  maxWidth: 400,
  width: '100%',
  position: 'relative',
  zIndex: 1,
  animation: 'cardIn 0.4s ease-out both',
};

const msButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 20px',
  backgroundColor: '#ffffff',
  color: '#5e5e5e',
  border: '1px solid #8c8c8c',
  borderRadius: 4,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'none',
  fontFamily: "'Segoe UI', sans-serif",
  width: '100%',
  transition: 'background 0.15s',
};
